import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import "flatpickr/dist/themes/material_blue.css";
import {
  Card,
  Col,
  Container,
  Row,
  CardBody,
  Label,
  Button,
  Form,
  Input,
} from "reactstrap";
import {
  getClinic,
  getRegion,
  getWardByRegion,
  updateClinic,
  getClinicById,
} from "service/api";

const defaultValue = {
  name: "",
  code: "",
  email: "",
  contactno: "",
  logo: "",
  region: "",
  ward: "",
  faculty_manager: "",
  services_rendered: "",
  address: "",
  comment: "",
};

const Editclinic = () => {
  const [clinic, setClinic] = useState(defaultValue);
  const [ward, setWard] = useState([]);
  const [Clinics, setClinics] = useState([]);
  const [file, setFile] = useState(null);
  const [region, setRegion] = useState([]);
  const [filePreview, setFilePreview] = useState(null);
  const [formRows, setFormRows] = useState([]);
  const [scheduleErrors, setScheduleErrors] = useState({});
  const [emailError, setEmailError] = useState("");

  const navigate = useNavigate();
  const { id } = useParams();

  // Load data
  useEffect(() => {
    const fetchInitial = async () => {
      try {
        const regionsRes = await getRegion();
        setRegion(regionsRes.data);

        const clinicsRes = await getClinic();
        setClinics(clinicsRes.data);

        const clinicRes = await getClinicById(id);
        const fetchedClinic = clinicRes.data;
        setClinic(fetchedClinic);

        if (fetchedClinic.logo) {
          setFilePreview(`${process.env.REACT_APP_API_URL}/${fetchedClinic.logo}`);
        }

        if (fetchedClinic.schedule && fetchedClinic.schedule.length > 0) {
          setFormRows(
            fetchedClinic.schedule.map((s, idx) => ({
              id: idx + 1,
              ...s,
            }))
          );
        } else {
          setFormRows([{ id: 1, from: "", to: "", duration: "", appointments: 0 }]);
        }

        if (fetchedClinic.region) {
          const wardRes = await getWardByRegion(fetchedClinic.region);
          setWard(wardRes.data);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchInitial();
  }, [id]);

  // Handle input changes
  const onValueChange = async (e) => {
    const { name, value, files } = e.target;

    if (name === "email") {
      const emailExists = Clinics.some(
        (c) => c.email === value && c._id !== id
      );
      if (emailExists) {
        setEmailError("This email is already in use.");
      } else {
        setEmailError("");
      }
    }

    if (name === "logo" && files && files.length > 0) {
      const selectedFile = files[0];
      setFile(selectedFile);
      setFilePreview(URL.createObjectURL(selectedFile));
    } else {
      setClinic({ ...clinic, [name]: value });
    }

    if (name === "region") {
      try {
        const fetchWard = await getWardByRegion(value);
        setWard(fetchWard.data);
      } catch (error) {
        console.error("Error fetching wards:", error);
      }
    }
  };

  // Schedule handling
  const calculateAppointments = (from, to, duration) => {
    const fromTime = new Date(`1970-01-01T${from}:00`);
    const toTime = new Date(`1970-01-01T${to}:00`);
    const durationMinutes = parseInt(duration);

    if (!isNaN(durationMinutes) && from && to) {
      const availableTime = (toTime - fromTime) / 60000;
      return Math.floor(availableTime / durationMinutes);
    } else {
      return 0;
    }
  };

  const checkDuplicate = (id, from, to) => {
    return formRows.some(
      (row) => row.id !== id && row.from === from && row.to === to
    );
  };

  const onFieldChange = (id, field, value) => {
    const modifiedRows = formRows.map((row) => {
      if (row.id === id) {
        const updatedRow = { ...row, [field]: value };

        if (["from", "to", "duration"].includes(field)) {
          updatedRow.appointments = calculateAppointments(
            updatedRow.from,
            updatedRow.to,
            updatedRow.duration
          );
        }

        if (updatedRow.from && updatedRow.to) {
          const isDuplicate = checkDuplicate(id, updatedRow.from, updatedRow.to);
          setScheduleErrors((prev) => ({
            ...prev,
            [id]: isDuplicate ? "This schedule slot already exists." : "",
          }));
          if (isDuplicate) {
            toast.error("This schedule slot already exists.", {
              position: "top-center",
              autoClose: 2000,
            });
          }
        } else {
          setScheduleErrors((prev) => ({ ...prev, [id]: "" }));
        }

        return updatedRow;
      }
      return row;
    });
    setFormRows(modifiedRows);
  };

  const onAddFormRow = () => {
    const newRow = {
      id: formRows.length + 1,
      from: "",
      to: "",
      duration: "",
      appointments: 0,
    };
    setFormRows([...formRows, newRow]);
  };

  const onDeleteFormRow = (id) => {
    if (id !== 1) {
      const modifiedRows = formRows.filter((row) => row.id !== id);
      setFormRows(modifiedRows);
      const updatedErrors = { ...scheduleErrors };
      delete updatedErrors[id];
      setScheduleErrors(updatedErrors);
    }
  };

  // Update clinic
const handleUpdateClinic = async (e) => {
  e.preventDefault();

  if (emailError) {
    toast.error("Fix the errors before saving.", {
      position: "top-center",
      autoClose: 2000,
    });
    return;
  }

  const hasErrors = Object.values(scheduleErrors).some((err) => err !== "");
  if (hasErrors) {
    toast.error("This schedule slot already exists.", {
      position: "top-center",
      autoClose: 2000,
    });
    return;
  }

  const formData = new FormData();
  
  // Add all clinic data except schedule
  Object.keys(clinic).forEach((key) => {
    if (key !== 'schedule') { // Exclude schedule from regular fields
      formData.append(key, clinic[key]);
    }
  });
  
  if (file) {
    formData.append("logo", file);
  }

  // ✅ FIXED: Add schedule as proper JSON object (not stringified twice)
  const scheduleData = formRows.map(({ id, ...rest }) => rest);
  formData.append("schedule", JSON.stringify(scheduleData));

  try {
    await updateClinic(id, formData);
    navigate("/clinic");
    toast.success("Practice updated successfully.", {
      position: "top-center",
      autoClose: 1500,
    });
  } catch (error) {
    console.error("Error updating clinic:", error);
    toast.error("Error updating practice.");
  }
};

  document.title = "Edit Practice | Form";

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h4 className="page-title">Edit Practice</h4>
            <Link to="/clinic" className="btn btn-danger">
              View Practice
            </Link>
          </div>
          <Row>
            <Card>
              <CardBody>
                <Form>
                  <Row>
                    {/* --- Practice details inputs --- */}
                    <Col md={3}>
                      <div className="mb-3">
                        <Label>Practice Name</Label>
                        <Input
                          type="text"
                          name="name"
                          value={clinic.name}
                          placeholder="Practice Name"
                          onChange={onValueChange}
                        />
                      </div>
                    </Col>
                    <Col md={3}>
                      <div className="mb-3">
                        <Label>Practice Code</Label>
                        <Input
                          type="text"
                          name="code"
                          value={clinic.code}
                          placeholder="Practice Code"
                          onChange={onValueChange}
                        />
                      </div>
                    </Col>
                    <Col md={3}>
                      <div className="mb-3">
                        <Label>Practice Email</Label>
                        <Input
                          type="email"
                          name="email"
                          value={clinic.email}
                          placeholder="Practice Email"
                          onChange={onValueChange}
                        />
                        {emailError && (
                          <div className="text-danger">{emailError}</div>
                        )}
                      </div>
                    </Col>
                    <Col md={3}>
                      <div className="mb-3">
                        <Label>Practice Contact No.</Label>
                        <Input
                          type="text"
                          name="contactno"
                          maxLength={11}
                          value={clinic.contactno}
                          placeholder="E.g - 27843938590"
                          onChange={onValueChange}
                        />
                      </div>
                    </Col>
                    <Col md={2}>
                      <div className="mb-3">
                        <Label>Practice Logo</Label>
                        <Input
                          type="file"
                          name="logo"
                          onChange={onValueChange}
                        />
                        {filePreview && (
                          <img
                            src={filePreview}
                            alt="Practice Logo Preview"
                            style={{
                              marginTop: "10px",
                              maxHeight: "100px",
                              border: "1px solid grey",
                              borderRadius: "4px",
                            }}
                          />
                        )}
                      </div>
                    </Col>
                    <Col lg={2}>
                      <div className="mb-3">
                        <Label>Region</Label>
                        <select
                          name="region"
                          className="form-control"
                          value={clinic.region}
                          onChange={onValueChange}
                        >
                          <option value="">Select</option>
                          {region.map((s) => (
                            <option key={s._id} value={s._id}>
                              {s.region}
                            </option>
                          ))}
                        </select>
                      </div>
                    </Col>
                    <Col lg={2}>
                      <div className="mb-3">
                        <Label>Ward</Label>
                        <select
                          name="ward"
                          className="form-control"
                          value={clinic.ward}
                          onChange={onValueChange}
                        >
                          <option value="">Select</option>
                          {ward.map((s) => (
                            <option key={s._id} value={s._ward}>
                              {s.ward}
                            </option>
                          ))}
                        </select>
                      </div>
                    </Col>
                    <Col md={3}>
                      <div className="mb-3">
                        <Label>Faculty Manager</Label>
                        <Input
                          type="text"
                          name="faculty_manager"
                          value={clinic.faculty_manager}
                          placeholder="Faculty Manager"
                          onChange={onValueChange}
                        />
                      </div>
                    </Col>
                    <Col md={3}>
                      <div className="mb-3">
                        <Label>Services Rendered</Label>
                        <Input
                          type="text"
                          name="services_rendered"
                          value={clinic.services_rendered}
                          placeholder="Services Rendered"
                          onChange={onValueChange}
                        />
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="mb-3">
                        <Label>Address</Label>
                        <Input
                          type="textarea"
                          name="address"
                          value={clinic.address}
                          placeholder="Address"
                          onChange={onValueChange}
                        />
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="mb-3">
                        <Label>Comments</Label>
                        <Input
                          type="textarea"
                          name="comment"
                          value={clinic.comment}
                          placeholder="Comments"
                          onChange={onValueChange}
                        />
                      </div>
                    </Col>
                  </Row>

                  {/* --- Schedule Section --- */}
                  <Row>
                    <Col xs={12}>
                      <Card>
                        <CardBody>
                          <h6 className="mb-4 card-title">Practice Schedule</h6>
                          <Form className="repeater" encType="multipart/form-data">
                            <div>
                              {(formRows || []).map((formRow) => (
                                <Row key={formRow.id}>
                                  <Col lg={2} className="mb-3">
                                    <label>From</label>
                                    <input
                                      type="time"
                                      className="form-control"
                                      value={formRow.from}
                                      onChange={(e) =>
                                        onFieldChange(formRow.id, "from", e.target.value)
                                      }
                                    />
                                  </Col>
                                  <Col lg={2} className="mb-3">
                                    <label>To</label>
                                    <input
                                      type="time"
                                      className="form-control"
                                      value={formRow.to}
                                      onChange={(e) =>
                                        onFieldChange(formRow.id, "to", e.target.value)
                                      }
                                    />
                                    {scheduleErrors[formRow.id] && (
                                      <div className="text-danger">
                                        {scheduleErrors[formRow.id]}
                                      </div>
                                    )}
                                  </Col>
                                  <Col lg={1} className="mb-3">
                                    <label>Duration</label>
                                    <input
                                      className="form-control"
                                      value={formRow.duration}
                                      onChange={(e) =>
                                        onFieldChange(
                                          formRow.id,
                                          "duration",
                                          e.target.value
                                        )
                                      }
                                    />
                                  </Col>
                                  <Col lg={2} className="mb-3">
                                    <label>Appointments</label>
                                    <input
                                      className="form-control"
                                      value={calculateAppointments(
                                        formRow.from,
                                        formRow.to,
                                        formRow.duration
                                      )}
                                      readOnly
                                    />
                                  </Col>
                                  <Col lg={1} className="align-self-center">
                                    <div className="d-grid">
                                      <input
                                        type="button"
                                        className="btn btn-primary"
                                        value="Delete"
                                        onClick={() => onDeleteFormRow(formRow.id)}
                                      />
                                    </div>
                                  </Col>
                                </Row>
                              ))}
                            </div>
                            <input
                              type="button"
                              className="btn btn-success mt-3 mt-lg-0"
                              value="Add"
                              onClick={onAddFormRow}
                            />
                          </Form>
                        </CardBody>
                      </Card>
                    </Col>
                  </Row>

                  <div>
                    <button
                      className="btn btn-primary w-md mx-2"
                      onClick={handleUpdateClinic}
                    >
                      Update
                    </button>
                  </div>
                </Form>
              </CardBody>
            </Card>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default Editclinic;
